/**
 * Asset Analysis Tab
 *
 * Performance breakdown by asset and token category.
 */

import { formatCurrency, cn } from "@/lib/utils";
import { useTradingAnalysis } from "../trading-analysis-context";
import { AnalysisCard } from "./shared/analysis-card";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { getCategoryColor } from "@nordex/core";

/** Header cell with dashed underline tooltip */
function HeaderTooltip({ 
  children, 
  tooltip 
}: { 
  children: React.ReactNode; 
  tooltip: string;
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={250}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <span className="border-b border-dashed border-muted-foreground/50 cursor-default select-none">
            {children}
          </span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={5}
            className={cn(
              "z-50 max-w-[280px] rounded-md px-3 py-2",
              "bg-card/95 text-foreground/90 backdrop-blur-sm",
              "border border-border dark:border-muted-foreground/20 shadow-lg",
              "animate-in fade-in-0 zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
            )}
          >
            <p className="text-xs leading-relaxed font-normal">{tooltip}</p>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
import { TrendingUpIcon, TrendingDownIcon } from "lucide-react";

export function AssetAnalysisTab() {
  const { filteredTrades, assetAnalysis } = useTradingAnalysis();

  const isEmpty = !filteredTrades.length;

  // Get top 5 and bottom 5 markets
  const topMarkets = assetAnalysis.byMarket.filter((m) => m.trades > 0).slice(0, 5);
  const bottomMarkets = assetAnalysis.byMarket
    .filter((m) => m.trades > 0 && m.pnl < 0)
    .slice(-5)
    .reverse();

  return (
    <div className="space-y-4">
      {/* Category Performance */}
      <AnalysisCard title="Performance by Category" subtitle="Which crypto sectors work best for you">
        <div className="space-y-3 relative">
          {isEmpty ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              Category performance will appear here after you make trades
            </div>
          ) : (
            assetAnalysis.byCategory
              .filter((c) => c.trades > 0)
              .map((category) => {
                const maxPnl = Math.max(
                  ...assetAnalysis.byCategory.map((c) => Math.abs(c.pnl)),
                  1
                );
                const barWidth = (Math.abs(category.pnl) / maxPnl) * 100;
                const isPositive = category.pnl >= 0;
                const categoryColor = getCategoryColor(category.id as any);

                return (
                  <div key={category.id} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: categoryColor }}
                        />
                        <span className="text-xs font-medium">{category.label}</span>
                      </div>
                      <span
                        className={cn(
                          "text-sm font-bold",
                          isPositive ? "text-bid-foreground" : "text-ask-foreground"
                        )}
                      >
                        {isPositive ? "+" : ""}${formatCurrency(category.pnl, 0)}
                      </span>
                    </div>
                    <div className="h-3 bg-muted/30 rounded overflow-hidden">
                      <div
                        className="h-full transition-all rounded"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: isPositive
                            ? `${categoryColor}99`
                            : `${categoryColor}66`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>
                        {category.trades} trades • {category.winRate.toFixed(0)}% WR
                      </span>
                      <span>
                        ROI: {category.roi >= 0 ? "+" : ""}
                        {category.roi.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                );
              })
          )}
        </div>
        <div className="text-[10px] text-muted-foreground mt-3 pt-3 border-t border-border/30">
          Focus on categories with positive ROI and high win rates. Consider reducing exposure to underperforming sectors.
        </div>
      </AnalysisCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Performers */}
        <AnalysisCard
          title="Top Performers"
          subtitle="Assets you trade well — consider focusing here"
          action={<TrendingUpIcon className="size-4 text-bid-foreground" />}
        >
          {isEmpty || topMarkets.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-4">
              {isEmpty ? "Top performers will appear here" : "No profitable assets yet"}
            </div>
          ) : (
            <div className="space-y-2">
              {topMarkets.map((market, i) => {
                const isPositive = market.pnl >= 0;
                return (
                  <div
                    key={market.id}
                    className="flex items-center gap-2 p-2 bg-muted/20 rounded-md"
                  >
                    <div className="w-5 h-5 rounded-full bg-bid-foreground/20 flex items-center justify-center text-[10px] font-bold text-bid-foreground">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {market.label}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {market.trades} trades • {market.winRate.toFixed(0)}% WR
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          "text-sm font-bold",
                          isPositive ? "text-bid-foreground" : "text-ask-foreground"
                        )}
                      >
                        {isPositive ? "+" : ""}${formatCurrency(market.pnl, 0)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        PF: {market.profitFactor === Infinity ? "∞" : market.profitFactor.toFixed(1)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </AnalysisCard>

        {/* Bottom Performers */}
        <AnalysisCard
          title="Underperformers"
          subtitle="Consider avoiding or reviewing your strategy"
          action={<TrendingDownIcon className="size-4 text-ask-foreground" />}
        >
          {isEmpty || bottomMarkets.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-4">
              {isEmpty ? "Underperformers will appear here" : "No losing assets - great job!"}
            </div>
          ) : (
            <div className="space-y-2">
              {bottomMarkets.map((market, i) => (
                <div
                  key={market.id}
                  className="flex items-center gap-2 p-2 bg-muted/20 rounded-md"
                >
                  <div className="w-5 h-5 rounded-full bg-ask-foreground/20 flex items-center justify-center text-[10px] font-bold text-ask-foreground">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">
                      {market.label}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {market.trades} trades • {market.winRate.toFixed(0)}% WR
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-ask-foreground">
                      ${formatCurrency(market.pnl, 0)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      PF: {market.profitFactor.toFixed(1)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AnalysisCard>
      </div>

      {/* All Assets Table */}
      <AnalysisCard title="All Assets" subtitle="Complete breakdown of every asset you've traded">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/30">
                <th className="text-left py-2 px-1 font-medium text-muted-foreground">
                  Asset
                </th>
                <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                  <HeaderTooltip tooltip="Total profit/loss for this asset">
                    P&L
                  </HeaderTooltip>
                </th>
                <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                  <HeaderTooltip tooltip="Number of completed trades">
                    Trades
                  </HeaderTooltip>
                </th>
                <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                  <HeaderTooltip tooltip="Percentage of winning trades">
                    Win %
                  </HeaderTooltip>
                </th>
                <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                  <HeaderTooltip tooltip="Profit Factor: gross profit ÷ gross loss. Above 1.0 is profitable.">
                    PF
                  </HeaderTooltip>
                </th>
                <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                  <HeaderTooltip tooltip="Total trading volume">
                    Volume
                  </HeaderTooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    Asset data will appear here after you make trades
                  </td>
                </tr>
              ) : (
                assetAnalysis.byMarket
                  .filter((m) => m.trades > 0)
                  .map((market) => {
                    const isPositive = market.pnl >= 0;
                    return (
                      <tr
                        key={market.id}
                        className="border-b border-border/10 hover:bg-muted/20"
                      >
                        <td className="py-2 px-1 font-medium">{market.label}</td>
                        <td
                          className={cn(
                            "py-2 px-1 text-right font-semibold",
                            isPositive ? "text-bid-foreground" : "text-ask-foreground"
                          )}
                        >
                          {isPositive ? "+" : ""}${formatCurrency(market.pnl, 0)}
                        </td>
                        <td className="py-2 px-1 text-right text-muted-foreground">
                          {market.trades}
                        </td>
                        <td
                          className={cn(
                            "py-2 px-1 text-right",
                            market.winRate >= 50
                              ? "text-bid-foreground"
                              : "text-ask-foreground"
                          )}
                        >
                          {market.winRate.toFixed(0)}%
                        </td>
                        <td
                          className={cn(
                            "py-2 px-1 text-right",
                            market.profitFactor >= 1
                              ? "text-bid-foreground"
                              : "text-ask-foreground"
                          )}
                        >
                          {market.profitFactor === Infinity
                            ? "∞"
                            : market.profitFactor.toFixed(1)}
                        </td>
                        <td className="py-2 px-1 text-right text-muted-foreground">
                          ${formatCurrency(market.volume, 0)}
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </AnalysisCard>
    </div>
  );
}

